import { ParticleEffect } from './ParticleEffect';
import type { Particle, IntensityColorScheme } from '../types';

/**
 * Event confetti/sparkles effect
 */
export class EventEffect extends ParticleEffect {
    private startTime = 0;
    private duration = 5000; // Default 5 seconds

    protected getDefaultVariant(): string {
        return 'celebration';
    }

    protected getDefaultColorScheme(): IntensityColorScheme {
        return {
            minimal: '#f9ca24',  // Yellow
            subtle: '#4ecdc4',   // Turquoise
            moderate: '#ff6b6b', // Red
            bold: '#eb4d4b',     // Crimson
        };
    }

    setDuration(ms: number): void {
        this.duration = ms;
    }

    init(width: number, height: number): void {
        const count = this.getParticleCount();
        this.particles = [];
        this.startTime = Date.now();

        for (let i = 0; i < count; i++) {
            this.particles.push(this.createConfetti(width, height));
        }
    }

    private createConfetti(width: number, height: number): Particle {
        const sizeMultiplier = this.getSizeMultiplier();
        const speedMultiplier = this.getSpeedMultiplier();
        const useIntensityColor = this.customization.intensityColors || this.customization.customColor;

        const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b', '#eb4d4b', '#6ab04c'];
        const particleColor = useIntensityColor
            ? this.getIntensityColor()
            : colors[Math.floor(Math.random() * colors.length)];

        const particle: Particle = {
            x: width / 2 + (Math.random() - 0.5) * 200, // Burst from center
            y: height / 2,
            vx: (Math.random() - 0.5) * 8 * speedMultiplier,
            vy: (-8 - Math.random() * 6) * speedMultiplier, // Shoot upward
            size: (4 + Math.random() * 6) * sizeMultiplier,
            opacity: 0.8 + Math.random() * 0.2,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.2,
            color: particleColor,
        };

        // Try to add custom image if available
        if (this.hasCustomImages()) {
            particle.image = this.getRandomImage();
        }

        return particle;
    }

    update(_deltaTime: number, _width: number, height: number): void {        // Ensure particles have images if they loaded after particle creation
        this.ensureParticleImages();
        const elapsed = Date.now() - this.startTime;

        // Auto-stop after duration
        if (elapsed > this.duration) {
            this.clear();
            return;
        }

        for (let i = 0; i < this.particles.length; i++) {
            const p = this.particles[i];

            // Apply gravity
            p.vy += 0.3;
            p.x += p.vx;
            p.y += p.vy;
            p.rotation += p.rotationSpeed;

            // Fade out over time
            p.opacity = Math.max(0, 1 - elapsed / this.duration);

            // Remove particles that are off screen and faded
            if (p.y > height + 10 && p.opacity < 0.1) {
                this.particles.splice(i, 1);
                i--;
            }
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const p of this.particles) {
            // Use custom image if available
            if (p.image && p.image.complete && p.image.naturalHeight > 0) {
                this.renderParticle(ctx, p);
            } else {
                // Default confetti rendering
                ctx.save();
                ctx.globalAlpha = p.opacity;
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rotation);

                ctx.fillStyle = p.color!;
                ctx.fillRect(-p.size / 2, -p.size, p.size, p.size * 2);

                ctx.restore();
            }
        }
    }
    isComplete(): boolean {
        return this.particles.length === 0 && Date.now() - this.startTime > this.duration;
    }
}